import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { US_STATES, CA_PROVINCES } from '../lib/api';
import { Sparkle, MapPin, Calendar, Car, Lightbulb, ArrowRight, Compass, ShareNetwork, Copy, Bed } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Polyline, Popup } from 'react-leaflet';
import L from 'leaflet';
import { toast } from 'sonner';
import Seo from '../components/Seo';

const homeIcon = L.divIcon({ className: '', html: '<div style="background:#1E3F20;width:30px;height:30px;border-radius:50%;border:3px solid #fff;box-shadow:0 4px 14px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:14px;">H</div>', iconSize: [30, 30], iconAnchor: [15, 15] });
const stopIcon = (i) => L.divIcon({ className: '', html: `<div style="background:#C05A3C;width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 4px 14px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;"><span style="transform:rotate(45deg);color:#fff;font-weight:700;font-size:14px;">${i+1}</span></div>`, iconSize: [32, 42], iconAnchor: [16, 42] });

export default function TripPlanner() {
  const today = new Date().toISOString().slice(0, 10);
  const inSixMonths = new Date(Date.now() + 180*86400_000).toISOString().slice(0, 10);
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState({
    home_city: params.get('city') || '',
    home_state: params.get('state') || '',
    start_date: params.get('start') || today,
    end_date: params.get('end') || inSixMonths,
    max_drive_per_day: parseInt(params.get('miles')) || 500,
    interests: params.get('interests') || '',
  });
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [hotelData, setHotelData] = useState({}); // {event_id: {cheapest, nights, subtotal}}
  const [hotelsLoading, setHotelsLoading] = useState(false);

  const computeNights = (start, end) => {
    if (!start) return 2;
    if (!end || end === start) return 1;
    const a = new Date(start), b = new Date(end);
    const n = Math.round((b - a) / 86_400_000);
    return Math.max(1, n);
  };

  // After a plan loads, fetch cheapest hotel near each stop (in parallel) to build a live cost estimate
  useEffect(() => {
    if (!plan?.stops?.length) { setHotelData({}); return; }
    setHotelsLoading(true);
    setHotelData({});
    Promise.all(plan.stops.map(async (s) => {
      const nights = computeNights(s.start_date, s.end_date);
      try {
        const r = await api.get('/hotels/search', {
          params: { lat: s.lat, lng: s.lng, city: s.city, checkin: s.start_date, checkout: s.end_date || s.start_date },
        });
        const hotels = (r.data?.results || []).filter(h =>
          typeof h.price === 'number' && h.price > 0 &&
          (h.distance_miles == null || h.distance_miles <= 50)
        );
        hotels.sort((a, b) => a.price - b.price);
        const cheapest = hotels[0] || null;
        return [s.event_id, {
          cheapest,
          nights,
          subtotal: cheapest ? Math.round(cheapest.price * nights) : null,
          source: r.data?.source,
          fallback: !!r.data?.fallback,
        }];
      } catch {
        return [s.event_id, { cheapest: null, nights, subtotal: null }];
      }
    })).then(entries => {
      setHotelData(Object.fromEntries(entries));
      setHotelsLoading(false);
    });
  }, [plan]);

  const lodgingTotal = Object.values(hotelData).reduce((sum, x) => sum + (x.subtotal || 0), 0);
  const hotelsWithPrice = Object.values(hotelData).filter(x => x.subtotal != null).length;

  const runPlan = async (f) => {
    setLoading(true); setPlan(null);
    try {
      const r = await api.post('/trip/plan', f);
      setPlan(r.data);
      if (!r.data.stops?.length) toast.info('No stops generated — try widening your date range or distance.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not plan trip');
    } finally { setLoading(false); }
  };

  // Auto-run on mount if URL has ?city=
  useEffect(() => {
    if (params.get('city') && !plan && !loading) {
      runPlan(form);
    }
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.home_city) return toast.error('Enter your home city');
    // Update URL so the trip is shareable
    const sp = new URLSearchParams();
    sp.set('city', form.home_city);
    if (form.home_state) sp.set('state', form.home_state);
    sp.set('start', form.start_date);
    sp.set('end', form.end_date);
    sp.set('miles', String(form.max_drive_per_day));
    if (form.interests) sp.set('interests', form.interests);
    setParams(sp, { replace: true });
    runPlan(form);
  };

  const copyShareLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Trip link copied — share it anywhere!');
    } catch {
      toast.error('Could not copy. Try again.');
    }
  };

  const nativeShare = async () => {
    const url = window.location.href;
    const title = `My ${plan?.stops.length}-stop pow wow road trip`;
    const text = plan?.summary?.slice(0, 200) || '';
    if (navigator.share) {
      try { await navigator.share({ title, text, url }); } catch {}
    } else {
      copyShareLink();
    }
  };

  // Build polyline points: home -> stops in order
  const route = plan ? [[plan.home.lat, plan.home.lng], ...plan.stops.map(s => [s.lat, s.lng])] : [];
  const center = plan && plan.stops.length ? [plan.stops[0].lat, plan.stops[0].lng] : [39.5, -98.35];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo
        path="/plan"
        title="Pow Wow Trip Planner"
        description="Plan a multi-stop pow wow road trip across the US and Canada. Build a smart itinerary that matches your interests, driving rhythm, and dates — with live hotel pricing."
        keywords="pow wow road trip, powwow itinerary, trip planner, native american travel, indigenous events trip"
      />
      <div className="mb-10">
        <p className="label text-terracotta mb-2 flex items-center gap-1"><Sparkle size={14} weight="duotone" /> Trip Planner</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Plan your pow wow road trip</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Tell us your home base and dates — we'll craft a multi-stop itinerary across Turtle Island, picking events that match your interests and fit your driving rhythm.</p>
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-8">
        <form onSubmit={submit} className="bg-card border border-border rounded-2xl p-6 h-fit lg:sticky lg:top-24 space-y-4" data-testid="trip-form">
          <div>
            <label className="label text-muted-foreground mb-2 block">Home city</label>
            <Input value={form.home_city} onChange={e => setForm({...form, home_city: e.target.value})} placeholder="e.g., Albuquerque" data-testid="trip-city" />
          </div>
          <div>
            <label className="label text-muted-foreground mb-2 block">State / Province</label>
            <Select value={form.home_state} onValueChange={v => setForm({...form, home_state: v})}>
              <SelectTrigger data-testid="trip-state"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {[...US_STATES, ...CA_PROVINCES].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label text-muted-foreground mb-2 block">From</label>
              <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} data-testid="trip-start" />
            </div>
            <div>
              <label className="label text-muted-foreground mb-2 block">To</label>
              <Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} data-testid="trip-end" />
            </div>
          </div>
          <div>
            <label className="label text-muted-foreground mb-2 block">Max drive per leg (mi)</label>
            <Input type="number" value={form.max_drive_per_day} onChange={e => setForm({...form, max_drive_per_day: +e.target.value})} data-testid="trip-miles" />
          </div>
          <div>
            <label className="label text-muted-foreground mb-2 block">Interests (optional)</label>
            <textarea
              rows={3}
              value={form.interests}
              onChange={e => setForm({...form, interests: e.target.value})}
              placeholder="big payouts, jingle dress, drum contests…"
              className="w-full bg-transparent border border-border rounded-xl p-3 text-sm"
              data-testid="trip-interests"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-forest hover:bg-forest/90 text-bone rounded-full h-11" data-testid="trip-submit">
            {loading ? 'Asking the guide…' : <><Compass size={18} className="mr-2" /> Plan my trip</>}
          </Button>
        </form>

        <div>
          {!plan && !loading && (
            <div className="bg-muted/40 rounded-2xl p-12 text-center border border-dashed border-border">
              <Sparkle size={48} weight="duotone" className="mx-auto text-terracotta mb-4" />
              <p className="font-display text-2xl mb-2">Your itinerary will appear here</p>
              <p className="text-muted-foreground text-sm max-w-md mx-auto">Built on 127+ real pow wows scraped from powwows.com.</p>
            </div>
          )}
          {loading && (
            <div className="bg-muted/40 rounded-2xl p-12 text-center">
              <div className="w-12 h-12 border-4 border-forest/20 border-t-forest rounded-full animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Routing your road trip…</p>
            </div>
          )}
          {plan && (
            <div className="space-y-6 fade-up" data-testid="trip-result">
              {plan.summary && (
                <div className="bg-forest text-bone rounded-2xl p-6">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <p className="label text-sand">Trip overview</p>
                    <div className="flex gap-2">
                      <button onClick={copyShareLink} className="text-xs text-bone/70 hover:text-sand flex items-center gap-1" data-testid="trip-copy-link">
                        <Copy size={14} weight="duotone" /> Copy link
                      </button>
                      <button onClick={nativeShare} className="text-xs text-bone/70 hover:text-sand flex items-center gap-1" data-testid="trip-share">
                        <ShareNetwork size={14} weight="duotone" /> Share
                      </button>
                    </div>
                  </div>
                  <p className="text-bone/90 leading-relaxed">{plan.summary}</p>
                  <div className="mt-4 flex flex-wrap gap-6 text-sm">
                    <span><Car size={14} weight="duotone" className="inline mr-1.5 text-sand" /> {plan.total_miles?.toLocaleString()} total miles</span>
                    <span><MapPin size={14} weight="duotone" className="inline mr-1.5 text-sand" /> {plan.stops.length} stops</span>
                    <span className="text-bone/60">From {plan.events_considered} events in window</span>
                  </div>
                  {/* Live lodging cost estimate */}
                  <div className="mt-4 pt-4 border-t border-bone/15">
                    {hotelsLoading ? (
                      <p className="text-sm text-bone/70 flex items-center gap-2" data-testid="lodging-loading">
                        <span className="w-3 h-3 border-2 border-sand/30 border-t-sand rounded-full animate-spin" />
                        Pricing lodging from Booking.com…
                      </p>
                    ) : lodgingTotal > 0 ? (
                      <div className="flex items-baseline justify-between flex-wrap gap-2" data-testid="lodging-total">
                        <div>
                          <p className="text-xs text-bone/70 uppercase tracking-wider">Est. lodging total</p>
                          <p className="font-display text-3xl text-gold">${lodgingTotal.toLocaleString()}</p>
                        </div>
                        <p className="text-xs text-bone/60 max-w-xs text-right">
                          Cheapest hotel × nights at each stop, live from Booking.com.{' '}
                          {hotelsWithPrice < plan.stops.length && (
                            <span className="text-crimson/80">({plan.stops.length - hotelsWithPrice} stop{plan.stops.length - hotelsWithPrice > 1 ? 's' : ''} couldn't be priced — see below.)</span>
                          )}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}

              {plan.stops.length > 0 && (
                <div className="bg-card border border-border rounded-2xl p-3">
                  <MapContainer center={center} zoom={4} style={{ height: '380px', borderRadius: '14px' }} scrollWheelZoom>
                    <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution="&copy; OSM &copy; CARTO" />
                    <Marker position={[plan.home.lat, plan.home.lng]} icon={homeIcon}><Popup>Home: {plan.home.city}</Popup></Marker>
                    {plan.stops.map((s, i) => (
                      <Marker key={s.event_id} position={[s.lat, s.lng]} icon={stopIcon(i)}>
                        <Popup>
                          <div className="p-2 w-56">
                            <p className="font-display text-base">{s.name}</p>
                            <p className="text-xs text-muted-foreground">{s.city}, {s.state} · {s.start_date}</p>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                    <Polyline positions={route} color="#C05A3C" weight={3} dashArray="6 8" />
                  </MapContainer>
                </div>
              )}

              <ol className="space-y-3">
                {plan.stops.map((s, i) => {
                  const h = hotelData[s.event_id];
                  return (
                  <li key={s.event_id} className="bg-card border border-border rounded-2xl p-5 flex gap-4" data-testid={`trip-stop-${i}`}>
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-terracotta text-white grid place-items-center font-display text-lg">{i + 1}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                        <Link to={`/events/${s.event_id}`} className="font-display text-xl hover:text-terracotta">{s.name}</Link>
                        <span className="text-xs text-muted-foreground"><Car size={12} className="inline" /> {s.leg_miles.toLocaleString()} mi from previous</span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        <Calendar size={12} className="inline mr-1" /> {s.start_date}{s.end_date !== s.start_date && ` – ${s.end_date}`} · <MapPin size={12} className="inline mr-1" />{s.city}, {s.state}
                        {s.total_prize_pool > 0 && <> · <strong className="text-gold">${s.total_prize_pool.toLocaleString()}</strong> pool</>}
                      </p>
                      {s.reason && <p className="text-sm italic mb-3">"{s.reason}"</p>}

                      {/* Live cheapest hotel suggestion */}
                      {h?.cheapest ? (
                        <a
                          href={h.cheapest.url}
                          target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 bg-navy/30 hover:bg-navy/50 border border-turquoise/20 rounded-xl p-3 transition group"
                          data-testid={`trip-hotel-${i}`}
                        >
                          {h.cheapest.image ? (
                            <img src={h.cheapest.image} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-14 h-14 rounded-lg bg-muted grid place-items-center flex-shrink-0"><Bed size={20} weight="duotone" className="text-turquoise" /></div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider"><Bed size={11} weight="duotone" className="inline mr-1" /> Stay suggestion · {h.nights} night{h.nights > 1 ? 's' : ''}</p>
                            <p className="font-medium truncate group-hover:text-turquoise">{h.cheapest.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {h.cheapest.distance_miles != null && <>{h.cheapest.distance_miles} mi · </>}
                              {h.cheapest.rating != null && <>★ {h.cheapest.rating} · </>}
                              <span className="text-gold font-semibold">${Math.round(h.cheapest.price)}/night</span>
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Subtotal</p>
                            <p className="font-display text-lg text-gold">${h.subtotal.toLocaleString()}</p>
                          </div>
                        </a>
                      ) : hotelsLoading ? (
                        <div className="h-14 bg-muted/40 rounded-xl animate-pulse" />
                      ) : h ? (
                        <p className="text-xs text-muted-foreground italic">No live hotel pricing available for this stop.</p>
                      ) : null}
                    </div>
                  </li>
                  );
                })}
              </ol>

              {plan.tips?.length > 0 && (
                <div className="bg-sand/15 border border-sand/30 rounded-2xl p-6">
                  <p className="label text-terracotta mb-3 flex items-center gap-1"><Lightbulb size={14} weight="duotone" /> Travel tips</p>
                  <ul className="space-y-2 text-sm">
                    {plan.tips.map((t, i) => (
                      <li key={i} className="flex gap-2"><ArrowRight size={14} className="mt-1 text-sand flex-shrink-0" /> <span>{t}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
